// src/utils/backdropDismiss.js
//
// Safe "click the backdrop to close" handlers for modals.
//
// A plain `onClick` on the overlay is not enough: the browser dispatches the
// click event on the nearest common ancestor of where the press started and
// where it was released. So a drag that begins inside the dialog (e.g. select
// text in an input) and ends on the backdrop produces a click whose target IS
// the overlay — the dialog closes mid-selection even though the click started
// inside it. stopPropagation on the dialog cannot prevent that.
//
// Spread these handlers on the overlay instead:
//
//   <div className="overlay" {...backdropDismiss(onClose)}>
//
// Dismissal only happens when the pointer press AND the release both land on
// the overlay itself, which also makes mouse, touch and pen behave the same.
// The dialog needs no stopPropagation for this to work (keeping it is fine).

const pressedOnBackdrop = new WeakSet();

/**
 * @param {() => void} onDismiss called when the backdrop is pressed and released.
 * @returns {{ onPointerDown: Function, onPointerUp: Function }}
 */
export function backdropDismiss(onDismiss) {
  return {
    onPointerDown: (event) => {
      if (event.target === event.currentTarget) {
        pressedOnBackdrop.add(event.currentTarget);
      } else {
        pressedOnBackdrop.delete(event.currentTarget);
      }
    },
    onPointerUp: (event) => {
      const startedOnBackdrop = pressedOnBackdrop.has(event.currentTarget);
      pressedOnBackdrop.delete(event.currentTarget);
      if (startedOnBackdrop && event.target === event.currentTarget) {
        onDismiss?.(event);
      }
    },
  };
}

export default backdropDismiss;
